import { Alert, Col, Container, Form, Row, Table } from 'react-bootstrap'
import { isUserLoggedIn, showKAlert } from '../helpers'
import { t } from '../locale'
import {
  VertecConnectionStatus,
  VertecImportCandidate,
  VertecObjectsResult,
  deleteVertecConnection,
  fetchVertecImportCandidates,
  fetchVertecObjects,
  getVertecConnectionStatus,
  saveVertecConnection,
  saveVertecImportCandidates,
} from '../service/vertec'
import { useState, useEffect } from 'react'
import KLayerNavigation from '../components/KLayerNavigation'
import KButton from '../components/KButton'

const emptyForm = { server: '', apiToken: '' }

export default function VertecSettings() {
  const [status, setStatus] = useState<VertecConnectionStatus | null>(null)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)

  const [importType, setImportType] = useState<'companies' | 'persons'>('companies')
  const [candidates, setCandidates] = useState<VertecImportCandidate[] | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [loadingCandidates, setLoadingCandidates] = useState(false)
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState<{ created: number; updated: number } | null>(
    null
  )

  useEffect(() => {
    void fetchStatus()
  }, [])

  const fetchStatus = async () => {
    try {
      setStatus(await getVertecConnectionStatus())
    } catch {
      setStatus(null)
    }
  }

  const save = async () => {
    if (!form.server.trim() || !form.apiToken.trim()) {
      showKAlert('danger', t('vertecAllFieldsRequired'))
      return
    }

    setSaving(true)
    try {
      const res = await saveVertecConnection({
        server: form.server.trim(),
        apiToken: form.apiToken.trim(),
      })
      if (res.ok) {
        setForm(emptyForm)
        showKAlert('success', t('vertecConnectionSaved'))
        await fetchStatus()
      }
    } finally {
      setSaving(false)
    }
  }

  const disconnect = async () => {
    const res = await deleteVertecConnection()
    if (res.ok) {
      setForm(emptyForm)
      setStatus({ configured: false })
    }
  }

  const loadCandidates = async () => {
    setLoadingCandidates(true)
    setCandidates(null)
    setSelectedIds(new Set())
    setImportResult(null)

    try {
      const res = await fetchVertecImportCandidates(importType)
      if (res.ok) setCandidates(res.candidates)
    } catch {
      showKAlert('danger', t('vertecLoadCandidatesFailed'))
    } finally {
      setLoadingCandidates(false)
    }
  }

  const toggleCandidate = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const toggleAllCandidates = () => {
    if (!candidates) return
    setSelectedIds(
      selectedIds.size === candidates.length ? new Set() : new Set(candidates.map(c => c.vertecId))
    )
  }

  const importSelectedCandidates = async () => {
    if (!candidates || selectedIds.size === 0) return
    setImporting(true)
    setImportResult(null)

    try {
      const selected = candidates.filter(c => selectedIds.has(c.vertecId))
      const res = await saveVertecImportCandidates(selected)

      if (res.ok) {
        setImportResult({ created: res.created, updated: res.updated })
        setSelectedIds(new Set())
      }
    } catch {
      showKAlert('danger', t('vertecImportFailed'))
    } finally {
      setImporting(false)
    }
  }

  const allCandidatesSelected =
    !!candidates && candidates.length > 0 && selectedIds.size === candidates.length

  return (
    <Container>
      <Row>
        <div className='sidebar col-md-3 col-lg-2 p-0'>
          <KLayerNavigation logged={isUserLoggedIn} />
        </div>
        <main className='ms-sm-auto col-lg-10 px-md-4 md-4'>
          <div className='col-12'>
            <p className='pe-none text-draft lead fw-bold link-offset-3 link-underline-primary'>
              {t('vertecSettingsTitle')}
            </p>
          </div>

          <div className='col-12 my-5'>
            <Row>
              <Col lg={4}>
                <div className='d-flex justify-between section-title-blade'>
                  <div className='pe-4 sm:px-0'>
                    <p className='mt-1 text-draft'>{t('vertecSettingsHint')}</p>
                  </div>
                </div>
              </Col>
              <Col lg={8}>
                <div className='px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md'>
                  {status?.configured && (
                    <Alert
                      variant='success'
                      className='d-flex justify-content-between align-items-center'
                    >
                      <span>
                        <i className='bi bi-check-circle-fill me-2'></i>
                        {t('vertecConnected')}
                        {status.server ? ` - ${status.server}` : ''}
                      </span>
                      <KButton
                        id='btn-vertec-disconnect'
                        label={t('vertecDisconnect')}
                        variant='outline-danger'
                        icon='x-circle'
                        size='sm'
                        onClick={disconnect}
                      />
                    </Alert>
                  )}
                  {status && !status.configured && (
                    <p className='text-muted'>{t('vertecNotConnected')}</p>
                  )}

                  <Form>
                    <Form.Group className='mb-3'>
                      <Form.Label className='fw-medium smaller text-primary'>
                        {t('vertecServer')}
                      </Form.Label>
                      <Form.Control
                        type='text'
                        placeholder='ihrefirma.vertec-cloud.com'
                        value={form.server}
                        onChange={e => setForm({ ...form, server: e.target.value })}
                      />
                    </Form.Group>
                    <Form.Group className='mb-3'>
                      <Form.Label className='fw-medium smaller text-primary'>
                        {t('vertecApiToken')}
                      </Form.Label>
                      <Form.Control
                        type='password'
                        autoComplete='new-password'
                        value={form.apiToken}
                        onChange={e => setForm({ ...form, apiToken: e.target.value })}
                      />
                    </Form.Group>
                    <KButton
                      id='btn-vertec-connect'
                      label={t('vertecSave')}
                      variant='primary'
                      icon='plug'
                      disabled={saving}
                      onClick={save}
                    />
                  </Form>

                  {/* {status?.configured && (
                    <div className='mt-5 pt-4 border-top'>
                      <p className='fw-medium smaller text-primary mb-2'>{t('vertecTestTitle')}</p>
                      <div className='d-flex gap-2 align-items-end mb-3'>
                        <Form.Group className='mb-0'>
                          <Form.Label className='fw-medium smaller text-primary'>
                            {t('vertecTestClass')}
                          </Form.Label>
                          <Form.Control
                            type='text'
                            value={testClassname}
                            onChange={e => setTestClassname(e.target.value)}
                            style={{ maxWidth: 240 }}
                          />
                        </Form.Group>
                        <Form.Group className='mb-0 flex-grow-1'>
                          <Form.Label className='fw-medium smaller text-primary'>
                            {t('vertecTestFilter')}
                          </Form.Label>
                          <Form.Control
                            type='text'
                            value={testQuery}
                            onChange={e => setTestQuery(e.target.value)}
                            placeholder='query_member0=aktiv&query_value0=true'
                          />
                        </Form.Group>
                        <KButton
                          id='btn-vertec-test'
                          label={t('vertecTestRun')}
                          variant='outline-primary'
                          icon='play-fill'
                          disabled={testing}
                          onClick={runTest}
                        />
                      </div>
                      {testResult && (
                        <>
                          <p className='text-muted small mb-2'>
                            {t('vertecTestCount')}: {testResult.count}
                          </p>
                          <pre
                            className='bg-light p-3 rounded'
                            style={{ maxHeight: 400, overflow: 'auto' }}
                          >
                            {JSON.stringify(testResult.data, null, 2)}
                          </pre>
                        </>
                      )}
                    </div>
                  )} */}

                  {status?.configured && (
                    <div className='mt-5 pt-4 border-top'>
                      <p className='fw-medium smaller text-primary mb-2'>
                        {t('vertecImportTitle')}
                      </p>
                      <p className='text-muted mb-3'>{t('vertecImportDescription')}</p>

                      <div className='d-flex gap-3 align-items-end mb-3'>
                        <Form.Group className='mb-0'>
                          <Form.Check
                            inline
                            type='radio'
                            label={t('vertecImportTypeCompanies')}
                            checked={importType === 'companies'}
                            onChange={() => setImportType('companies')}
                          />
                          <Form.Check
                            inline
                            type='radio'
                            label={t('vertecImportTypePersons')}
                            checked={importType === 'persons'}
                            onChange={() => setImportType('persons')}
                          />
                        </Form.Group>
                        <KButton
                          id='btn-vertec-load-candidates'
                          label={
                            loadingCandidates
                              ? t('vertecLoadingCandidates')
                              : t('vertecLoadCandidates')
                          }
                          variant='secondary'
                          icon='people'
                          disabled={loadingCandidates}
                          onClick={loadCandidates}
                        />
                      </div>

                      {candidates !== null && (
                        <>
                          {candidates.length === 0 ? (
                            <p className='text-muted'>{t('vertecNoCandidates')}</p>
                          ) : (
                            <>
                              <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                                <Table hover size='sm' className='mb-0'>
                                  <thead className='table-light'>
                                    <tr>
                                      <th style={{ width: 40 }}>
                                        <Form.Check
                                          checked={allCandidatesSelected}
                                          onChange={toggleAllCandidates}
                                        />
                                      </th>
                                      <th>{t('name')}</th>
                                      <th>{t('type')}</th>
                                      <th>{t('address')}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {candidates.map(c => (
                                      <tr
                                        key={c.vertecId}
                                        onClick={() => toggleCandidate(c.vertecId)}
                                        style={{ cursor: 'pointer' }}
                                      >
                                        <td>
                                          <Form.Check
                                            checked={selectedIds.has(c.vertecId)}
                                            onChange={() => toggleCandidate(c.vertecId)}
                                            onClick={e => e.stopPropagation()}
                                          />
                                        </td>
                                        <td>{c.firstName ? `${c.firstName} ${c.name}` : c.name}</td>
                                        <td>
                                          {c.isPerson
                                            ? t('vertecImportTypePersons')
                                            : t('vertecImportTypeCompanies')}
                                        </td>
                                        <td>
                                          {[c.streetAddress, c.zipCode, c.city]
                                            .filter(Boolean)
                                            .join(', ') || '-'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>

                              <div className='mt-3 d-flex align-items-center gap-3'>
                                <KButton
                                  id='btn-vertec-import'
                                  label={
                                    importing
                                      ? t('vertecImporting')
                                      : `${t('vertecImportSelected')} (${selectedIds.size})`
                                  }
                                  variant='primary'
                                  icon='download'
                                  disabled={importing || selectedIds.size === 0}
                                  onClick={importSelectedCandidates}
                                />
                                {importResult && (
                                  <span className='text-success'>
                                    {t('vertecImportResult')}: {importResult.created}{' '}
                                    {t('bexioCreated')}, {importResult.updated} {t('bexioUpdated')}
                                  </span>
                                )}
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </div>
        </main>
      </Row>
    </Container>
  )
}
